import React, { useContext, useEffect, useState } from "react";
import * as FP from "utils/fp-js";
// import "./Dashboard.css";
import { EndIcon } from "./Dashboard.style";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import { showError } from "store/reducers/errorReducer";

export const NUMBER_OF_LEVELS = Object.keys(EndIcon).length;
export const REFERRALS_IN_LEVEL = 50;

export const STARTER_MAX_PROGRESS = 20;
export const BRONZE_MAX_PROGRESS = 40;
export const SILVER_MAX_PROGRESS = 60;
export const GOLD_MAX_PROGRESS = 80;

export const mobileModes = {
  Profile: "Profile",
  Awards: "Awards",
  Dashboard: "Dashboard",
};

export const getUserLevel = (progress) =>
  FP.match(true)
    .on(progress >= 100, "Daimond")
    .on(progress >= GOLD_MAX_PROGRESS, "Platinum")
    .on(progress >= SILVER_MAX_PROGRESS, "Gold")
    .on(progress >= BRONZE_MAX_PROGRESS, "Silver")
    .on(progress >= STARTER_MAX_PROGRESS, "Bronze")
    .otherwise("Starter");

export const getUserPosition = (progress) =>
  FP.match(true)
    .on(progress >= 100, 5)
    .on(progress >= GOLD_MAX_PROGRESS, 10)
    .on(progress >= SILVER_MAX_PROGRESS, 20)
    .on(progress >= BRONZE_MAX_PROGRESS, 30)
    .on(progress >= STARTER_MAX_PROGRESS, 35)
    .otherwise(100);

export const getUserProgress = (referralsCount) =>
  (referralsCount * 100) / (REFERRALS_IN_LEVEL * NUMBER_OF_LEVELS);

export const useDashboard = () => {
  const { senderData, showWarning, hideWarning, warning, isGreetingsRead } =
    useContext(GlobalContext);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState("");
  const [originator, setOriginator] = useState(null);
  const [buttonLocations, setButtonLocations] = useState([]);
  const [senders, setSenders] = useState([]);
  const [providers, setProviders] = useState([]);
  const [progress, setProgress] = React.useState(0);
  const [levelProgress, setLevelProgress] = React.useState(0);
  const [newLevel, setNewLevel] = useState("");
  const [newCountsForUpdate, setNewCountsForUpdate] = useState("");
  const [logos, setLogos] = useState([]);
  const [modes, setModes] = useState([]);
  const [currentButton, setCurrentButton] = useState(null);
  const [isButtonAdderOpen, setIsButtonAdderOpen] = useState(false);

  const [oneColMode, setOneColRowMode] = useState(true);

  const addNewProvider = async (newProvider) => {
    if (!newProvider) return;
    const newProviders = [...providers, newProvider].sort((a, b) => {
      const nameA = a?.name?.toUpperCase();
      const nameB = b?.name?.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
    setProviders(newProviders);
  };

  const addNewSender = async (newSender) => {
    if (!newSender) return;
    const newSenders = [...senders, newSender].sort((a, b) => {
      const nameA = a?.firstName?.toUpperCase();
      const nameB = b?.firstName?.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
    setSenders(newSenders);
  };

  const toggleColMode = (mode) => {
    window.localStorage.setItem(
      `${originator?.id}-defaultColMode`,
      mode ? "true" : "false"
    );
    setOneColRowMode(mode);
  };

  const setError = (error) => dispatch(showError(error));

  const setNewProgress = (referralsCount) => {
    if (referralsCount > 0) {
      const currenProgress = Math.round(
        (referralsCount * 100) / (REFERRALS_IN_LEVEL * NUMBER_OF_LEVELS)
      );

      const newLevelProgress = Math.round(
        ((referralsCount % REFERRALS_IN_LEVEL) / REFERRALS_IN_LEVEL) * 100
      );

      setLevelProgress(currenProgress >= 100 ? 100 : newLevelProgress);
      setProgress(currenProgress <= 100 ? currenProgress : 100);
    }
  };

  const loadLogo = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/download_logo/${id}`,
        {
          method: "GET",
        }
      );
      const result = await response.json();

      if (!result?.error) {
        return result;
      }
      return undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  useEffect(() => {
    if (originator?.id) {
      const defaultColMode =
        window.localStorage.getItem(`${originator?.id}-defaultColMode`) !==
        "false";
      setOneColRowMode(defaultColMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originator]);

  useEffect(() => {
    const loadAllLogo = async () => {
      if (buttonLocations?.length) {
        try {
          const newLogos = [];
          const newModes = [];
          for (const buttonLocation of buttonLocations) {
            const locationId = Object.keys(buttonLocation)[0];
            const { recipient, imageFromS3 } = buttonLocation[locationId];
            if (imageFromS3?.awsKey) {
              const imageUrl = imageFromS3?.awsKey
                ? `${process.env.REACT_APP_API_V2_URL}/organizations/skins/${recipient?.id}`
                : null;
              newLogos.push(imageUrl);
              newModes.push(imageFromS3?.mode);
            } else {
              const { image, mode } = await loadLogo(recipient?.id);
              newLogos.push(image);
              newModes.push(mode);
            }
          }

          setLogos(newLogos);
          setModes(newModes);
        } catch (error) {
          console.error(error);
        }
      }
    };

    loadAllLogo();
  }, [buttonLocations]);

  const checkToken = async () => {
    if (!senderData?.email) {
      return;
    }
    dispatch(showSpinner());
    const accessToken = senderData?.dashboardAccessToken;

    if (!accessToken) {
      // @ts-ignore
      setNotification("You should get your button first.");
      setLoading(false);
      dispatch(hideSpinner());
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check_access_token`,
        {
          method: "POST",
          body: JSON.stringify({ accessToken }),
        }
      );
      const result = await response.json();

      if (result?.error || result?.message) {
        setError(result?.error || result?.message);
        return;
      }

      if (!result?.isTokenValid) {
        throw new Error();
      }

      setOriginator(result?.originator ?? null);
      // setReferrals(result?.referrals ?? []);
      const sortedLocations = (result?.buttonLocations ?? []).sort((a, b) => {
        const aKey = Object.keys(a)[0];
        const bKey = Object.keys(b)[0];
        const nameA = a[aKey]?.recipient?.name?.toUpperCase();
        const nameB = b[bKey]?.recipient?.name?.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0; // names must be equal
      });

      setSenders(result?.senders ?? []);
      setProviders(result?.providers ?? []);
      setButtonLocations(sortedLocations ?? []);
      setNewProgress(result?.referralsCount ?? 0);
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setLoading(false);
      dispatch(hideSpinner());
    }
  };

  useEffect(() => {
    checkToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderData?.email]);

  const userLevel = getUserLevel(progress);

  const updateSendersInButtonLocations = (locationId, senders) => {
    const newButtonLocations = buttonLocations.map((buttonLocation) => {
      const buttonLocationId = Object.keys(buttonLocation)[0];
      if (buttonLocationId === locationId) {
        return {
          [buttonLocationId]: {
            ...buttonLocation[buttonLocationId],
            senders,
          },
        };
      }
      return buttonLocation;
    });

    setButtonLocations(newButtonLocations);
  };

  return {
    loading,
    setError,
    setProgress,
    notification,
    originator,
    buttonLocations,
    progress,
    levelProgress,
    newLevel,
    newCountsForUpdate,
    logos,
    modes,
    isGreetingsRead,
    oneColMode,
    toggleColMode,
    showWarning,
    hideWarning,
    warning,
    setNewProgress,
    userLevel,
    setNewLevel,
    setNewCountsForUpdate,
    setLevelProgress,
    senders,
    providers,
    addNewProvider,
    addNewSender,
    updateSendersInButtonLocations,
    currentButton,
    setCurrentButton,
    isButtonAdderOpen,
    setIsButtonAdderOpen,
    reloadButtons: () => checkToken(),
  };
};
